import { Outlet } from 'react-router-dom'
import { VersionFooter } from './VersionFooter'

function WhaleIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <ellipse cx="32" cy="34" rx="24" ry="18" className="fill-whale-500" />
      <ellipse cx="32" cy="38" rx="18" ry="10" className="fill-whale-300" />
      <path
        d="M8 34C8 34 4 26 2 22C0 18 4 18 8 22C8 26 8 30 8 34Z"
        className="fill-whale-500"
      />
      <path
        d="M8 34C8 34 4 42 2 46C0 50 4 50 8 46C8 42 8 38 8 34Z"
        className="fill-whale-500"
      />
      <circle cx="44" cy="30" r="3" className="fill-white" />
      <circle cx="45" cy="29" r="1.5" className="fill-slate-900" />
      <path
        d="M32 16C32 16 30 10 28 6M32 16C32 16 34 10 36 6M32 16C32 16 32 9 32 4"
        stroke="#60a5fa"
        strokeWidth="2"
        strokeLinecap="round"
        className="stroke-whale-400"
      />
      <circle cx="50" cy="18" r="10" className="fill-amber-400" />
      <path
        d="M50 12C47.5 12 45.5 14 45.5 16.5V19.5C45.5 20.5 44.5 21.5 44 22H56C55.5 21.5 54.5 20.5 54.5 19.5V16.5C54.5 14 52.5 12 50 12Z"
        className="fill-amber-600"
      />
      <circle cx="50" cy="24" r="1.5" className="fill-amber-600" />
    </svg>
  )
}

export function AuthLayout() {
  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Background glowing orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-whale-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-whale-600/15 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex pb-8">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
          <div className="max-w-md text-white">
            <div className="flex items-center gap-3 mb-8">
              <WhaleIcon className="w-14 h-14" />
              <span className="text-3xl font-bold text-white">AlertWhale</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 leading-tight">
              Push Notifications &{' '}
              <span className="bg-gradient-to-r from-whale-400 via-blue-400 to-whale-400 bg-clip-text text-transparent">
                Live Activities
              </span>{' '}
              at Global Scale
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Send push notifications and live updates to iOS and Android devices with a single API.
              Built on Cloudflare's edge network for instant, reliable delivery worldwide.
            </p>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-3 gap-6">
              <div>
                <div className="text-2xl font-bold text-white">99.99%</div>
                <div className="text-sm text-slate-500">Uptime SLA</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">&lt;50ms</div>
                <div className="text-sm text-slate-500">Latency</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">200+</div>
                <div className="text-sm text-slate-500">Edge Locations</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
              <WhaleIcon className="w-12 h-12" />
              <span className="text-2xl font-bold text-white">AlertWhale</span>
            </div>

            {/* Glass card wrapper for auth forms */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1 shadow-2xl">
              <Outlet />
            </div>

            {/* Footer text */}
            <p className="text-center text-sm text-slate-500 mt-6">
              By signing in, you agree to our{' '}
              <a href="https://alertwhale.com/terms" className="text-whale-400 hover:text-whale-300">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="https://alertwhale.com/privacy" className="text-whale-400 hover:text-whale-300">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      <VersionFooter />
    </div>
  )
}
